import { useEffect, useState } from 'react';
import { EMOJI_SET } from '@sf72/shared';
import { useMatch } from '../store/match.js';
import { Button } from './primitives.js';

/**
 * Match controls: resign, draw, undo, and reactions.
 *
 * When there is an open offer aimed at this player, the row turns into the
 * answer to that offer — otherwise both players could have proposals in flight
 * and it stops being obvious what a tap does.
 */
export function Controls() {
  const match = useMatch((s) => s.match);
  const yourSeat = useMatch((s) => s.yourSeat);
  const offer = useMatch((s) => s.offer);
  const resign = useMatch((s) => s.resign);
  const offerDraw = useMatch((s) => s.offerDraw);
  const answerDraw = useMatch((s) => s.answerDraw);
  const requestUndo = useMatch((s) => s.requestUndo);
  const answerUndo = useMatch((s) => s.answerUndo);
  const sendEmoji = useMatch((s) => s.sendEmoji);

  /**
   * Resigning takes two taps.
   *
   * It ends the match, it is rated, and it cannot be undone — and it sits in a
   * row of three, one thumb-width from a button that does nothing worse than
   * ask a question. A confirmation that times out costs a careful player one
   * extra tap and saves a careless one their game.
   */
  const [confirmingResign, setConfirmingResign] = useState(false);

  useEffect(() => {
    if (!confirmingResign) return;
    const timer = window.setTimeout(() => setConfirmingResign(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirmingResign]);

  if (!match || yourSeat === null || match.status !== 'active') return null;

  if (offer && offer.fromSeat !== yourSeat && offer.kind !== 'rematch') {
    const label = offer.kind === 'draw' ? 'Durrang taklif qilyapti' : 'Yurishni qaytarmoqchi';
    const answer = offer.kind === 'draw' ? answerDraw : answerUndo;

    return (
      <div className="flex flex-col gap-2 rounded-2xl bg-surface p-3">
        <p className="text-[14px]">🤝 {label}</p>
        <div className="flex gap-2">
          <Button onClick={() => answer(true)} full>
            Roziman
          </Button>
          <Button variant="secondary" onClick={() => answer(false)} full>
            Yo‘q
          </Button>
        </div>
      </div>
    );
  }

  const waiting = offer?.fromSeat === yourSeat;

  // Only six of the thirty games can take a move back. Offering it elsewhere
  // sends both players through an agreement the server then has to refuse.
  const canAskForUndo = match.supportsUndo && match.seq > 0;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {EMOJI_SET.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => sendEmoji(emoji)}
            className="shrink-0 rounded-full bg-surface px-3 py-1.5 text-lg active:scale-90"
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Button
          variant="danger"
          onClick={() => {
            if (!confirmingResign) {
              setConfirmingResign(true);
              return;
            }
            setConfirmingResign(false);
            resign();
          }}
          full
        >
          {confirmingResign ? '🏳️ Rostdanmi?' : '🏳️ Taslim'}
        </Button>
        <Button variant="secondary" onClick={offerDraw} disabled={waiting} full>
          🤝 Durrang
        </Button>
        {canAskForUndo && (
          <Button variant="secondary" onClick={requestUndo} disabled={waiting} full>
            ↩️ Qaytarish
          </Button>
        )}
      </div>

      {waiting && (
        <p className="text-center text-[13px] text-hint">Raqib javobini kutyapmiz…</p>
      )}
    </div>
  );
}
